import { useEffect, useMemo, useState } from 'react';
import { t } from 'i18next';
import { useAppSelector } from './useAppSelector';
import { measureTextWidth } from '../util/measureTextWidth';
import { formatColumnValue } from '../util/formatColumnValue';
import { numberFormatter } from '../util/numberFormatter';
import {
  TABLE_HEADER_FONT,
  TABLE_BODY_FONT,
  TABLE_HEADER_EXTRA_WIDTH,
  TABLE_BODY_EXTRA_WIDTH,
} from '../constant/constants';
import { StatsTableColumn } from '../enum/TableColumn';

const EMPTY_FOOTER_VALUES: (string | number)[] = [];

/**
 * Computes the pixel width a "number_column" table_column needs so that
 * neither the header label, any body value (across the full dataset,
 * not just the currently virtualized rows), nor any footer sum
 * (Expand Sum / View Per Currency) gets clipped.
 *
 * `footerValues` should only be passed while a sum footer is visible,
 * otherwise stale sums would keep the column wide.
 */
export const useNumberColumnWidth = (
  columnName: string,
  data: Record<string, number>[],
  footerValues: (string | number)[] = EMPTY_FOOTER_VALUES,
): number => {
  const format = useAppSelector((state) => state.configSlice.format);

  const [fontsReady, setFontsReady] = useState(
    () => !document.fonts || (document.fonts.check(TABLE_BODY_FONT) && document.fonts.check(TABLE_HEADER_FONT)),
  );

  useEffect(() => {
    if (fontsReady || !document.fonts) return;

    let isMounted = true;

    // document.fonts.status / fonts.ready can report "loaded" before Inter has even been
    // requested, and canvas measuring doesn't trigger the download, so request the exact
    // fonts used for measurement explicitly and recompute once they are available.
    Promise.all([document.fonts.load(TABLE_BODY_FONT), document.fonts.load(TABLE_HEADER_FONT)])
      .catch(() => undefined)
      .then(() => {
        if (isMounted) setFontsReady(true);
      });

    return () => {
      isMounted = false;
    };
  }, [fontsReady]);

  const baseWidth = useMemo(() => {
    const headerLabel = t(`columns.${columnName}`);
    const headerWidth = measureTextWidth(headerLabel, TABLE_HEADER_FONT) + TABLE_HEADER_EXTRA_WIDTH;

    const noStatisticsLabel = `(${t('statisticPage.table.noStatistics')})`;

    // The percentage column renders a trailing " %" next to the number.
    const numberSuffix = columnName === StatsTableColumn.PERCENTAGE ? ' %' : '';

    // Mirrors TableBody + TableCellWithTooltip's rendering logic exactly,
    // so the measured text matches what actually ends up on screen.
    const bodyWidth = data.reduce((max, row) => {
      const formatted = formatColumnValue(columnName, row[columnName]);
      const displayValue =
        typeof formatted === 'number'
          ? `${numberFormatter(formatted, format)}${numberSuffix}`
          : formatted
            ? formatted
            : noStatisticsLabel;
      const width = measureTextWidth(String(displayValue), TABLE_BODY_FONT);

      return width > max ? width : max;
    }, 0);

    return Math.max(headerWidth, bodyWidth + TABLE_BODY_EXTRA_WIDTH);
    // fontsReady is intentionally in the dependency array so widths are
    // recomputed once Inter has actually loaded (canvas falls back to a
    // generic font otherwise, under-measuring the real rendered width).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnName, data, format, fontsReady]);

  // useViewPerCurrency returns a new array on every render, so memoize on its contents.
  const footerKey = footerValues.join('|');

  const footerWidth = useMemo(() => {
    const widest = footerValues.reduce<number>((max, value) => {
      const displayValue = ` ${formatColumnValue(columnName, value, format)}`;
      const width = measureTextWidth(displayValue, TABLE_BODY_FONT);

      return width > max ? width : max;
    }, 0);

    return widest > 0 ? widest + TABLE_BODY_EXTRA_WIDTH : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnName, footerKey, format, fontsReady]);

  return Math.ceil(Math.max(baseWidth, footerWidth));
};
